import { BettingApi } from 'api';
import cn from 'classnames';
import { DSAccordion } from 'designSystem/DSAccordion';
import { MarketTypesState } from 'features/eventLive/interface/interface';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation, withTranslation, WithTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { addNotification } from 'shared/components/toast';
import { isMobile, OUTCOME_NAME_DRAW, setAllBetsBetBuild } from 'shared/util/utility';
import { betbuilderEvents, setDataLayer } from '../../../shared/constants/datalayer';
import { Betbuilder, BetbuilderOutcome, EventState, GamesState } from '../interface/sportPrematch';

interface UIState {
	marketTypesList: MarketTypesState[];
}

interface BetBuilderProps extends WithTranslation {
	customeBets: number[];
	eventDetailData: EventState;
	setBetBuilderArray: (items: GamesState[]) => void;
	selectedOddsArray: GamesState[];
	setSelectedOddsArray: Dispatch<SetStateAction<GamesState[]>>;
	totalOdds: number;
	setTotalOdds: Dispatch<SetStateAction<number>>;
	conflictedIds: number[];
	setConflictedIds: Dispatch<SetStateAction<number[]>>;
}

const BetBuilder = ({
	customeBets,
	eventDetailData,
	setBetBuilderArray,
	selectedOddsArray,
	setSelectedOddsArray,
	setTotalOdds,
	conflictedIds,
	setConflictedIds,
}: BetBuilderProps) => {
	const { t } = useTranslation();
	const location = useLocation();
	const elementRef = useRef<HTMLDivElement>(null);

	const [isData, setIsData] = useState<boolean>(false);
	const [marketTypesList, setMarketTypesList] = useState<MarketTypesState[]>([]);

	const gamesObj = eventDetailData.games.reduce((gamesObj, game) => {
		gamesObj[game.gameId] = game;
		return gamesObj;
	}, {} as Record<number, GamesState>);

	useEffect(() => {
		return () => setDataLayer(betbuilderEvents.DATALAYER_LEFT_BET_BUILDER_VIEW);
	}, []);

	// eslint-disable-next-line @typescript-eslint/no-inferrable-types
	const getOdds = useCallback(
		async (selectedOddsArrayParam: GamesState[], updateState = false) => {
			if (selectedOddsArrayParam.length <= 1) {
				setSelectedOddsArray(selectedOddsArrayParam);
				setBetBuilderArray(selectedOddsArrayParam);
				setTotalOdds(0);
				setConflictedIds([]);
				return;
			}
			const response = await BettingApi.validationOdds(selectedOddsArrayParam.map((item) => item.outcomeId));
			if (response.data.code === 400) {
				addNotification('info', t('informbetBuilderMaxCouponsLimitation'));
			}
			if (response.data.data.validationResult.odds !== -1) {
				const conflictedOutcomeIds: number[] = [];
				response.data.data.validationResult.availableGames.forEach((outcomes: Betbuilder) => {
					outcomes.outcomes.forEach((outcome: BetbuilderOutcome) => {
						if (outcome.conflict) {
							conflictedOutcomeIds.push(outcome.outcomeId);
						}
					});
				});
				setConflictedIds(conflictedOutcomeIds);
			}
			if (response.data.data.validationResult.odds === -1) {
				addNotification('warning', t('betBuilderExclusiveOddsWarning'));
			} else {
				setSelectedOddsArray(selectedOddsArrayParam);
				setTotalOdds(response.data.data.validationResult.odds);
				setBetBuilderArray(selectedOddsArrayParam);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[setBetBuilderArray, t],
	);

	const marketTypesInit = useCallback(() => {
		const marketTypesList = eventDetailData.marketTypes.reduce(
			(marketTypesList: UIState['marketTypesList'], marketType: UIState['marketTypesList'][0]) => {
				const newMarketType = { ...marketType };
				newMarketType.gameIds = newMarketType.gameIds.filter((gameId: number) => customeBets.includes(gameId));

				if (newMarketType.gameIds.length) {
					marketTypesList.push(newMarketType);
				}

				return marketTypesList;
			},
			[],
		);

		if (selectedOddsArray.length > 1) {
			getOdds(selectedOddsArray, true);
		}
		setMarketTypesList(marketTypesList);
		setIsData(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [customeBets, eventDetailData.marketTypes, getOdds]);

	const onClickOdds = async (item: number, game: GamesState) => {
		const selectedOddsArrayVar = setAllBetsBetBuild([...selectedOddsArray], { ...game, outcomeId: item });
		getOdds(selectedOddsArrayVar, selectedOddsArrayVar.length < selectedOddsArray.length);
	};

	const checkOuteComeIsSelected = (gameId: number) => {
		if (selectedOddsArray.find((item) => item.outcomeId === gameId)) {
			return true;
		}
		return false;
	};

	useEffect(() => {
		setSelectedOddsArray([]);
		setTotalOdds(0);
		setBetBuilderArray([]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [setBetBuilderArray, location]);

	useEffect(() => {
		marketTypesInit();
	}, [marketTypesInit]);

	return (
		<>
			{isData && (
				<div ref={elementRef} id="BetBuilder" className={cn('bet-builder', { 'bet-builder-desktop': !isMobile() })}>
					{marketTypesList.map((marketType) => (
						<DSAccordion
							key={t(`betBuilder_marketType_${marketType.marketType}`)}
							settings={{
								stateType: 'prematch',
								stateKey: eventDetailData.sportId,
								stateValue: marketType.marketType,
								defaultCollapsed: true,
							}}
							titleTag="h1"
							className="mb-4"
							type="secondary"
							title={t(`marketType_${marketType.marketType}`)}
						>
							<>
								{marketType.gameIds.map((gameId) => (
									<div className="bet-builder-card" key={`gameIds-${gameId}`}>
										<div className="bet-builder-card-top">
											<p className="bet-name">{gamesObj[gameId]?.gameName}</p>
										</div>
										<div
											className={`card__buttons user-select-none ${
												gamesObj[gameId]?.outcomes.length === 3 ? 'three-button' : ''
											} ${
												// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
												gamesObj[gameId]?.outcomes.length && gamesObj[gameId]!.outcomes.length > 3
													? ' justify__content--start flex--wrap'
													: 'justify__content--between'
											}`}
										>
											{gamesObj[gameId]?.outcomes.map((outcome) => (
												<div
													key={`$outcomes-${outcome.outcomeId}`}
													// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
													onClick={() => onClickOdds(outcome.outcomeId, gamesObj[gameId]!)}
													className={cn('btn', {
														// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
														'width--full': gamesObj[gameId]!.outcomes.length === 1,
														// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
														'mb--5': gamesObj[gameId]!.outcomes.length > 3,
														[`point__card--${checkOuteComeIsSelected(outcome.outcomeId) ? 'primary' : 'light'}`]: true,
														'bet-builder__disabled': conflictedIds.includes(outcome.outcomeId),
													})}
												>
													<p className={`odds-text btn__padding ${isMobile() && 'truncate--text'}`}>
														<span>
															{outcome.outcomeName && outcome.outcomeName === OUTCOME_NAME_DRAW
																? t('draw')
																: outcome.outcomeName}
														</span>
													</p>
												</div>
											))}
										</div>
									</div>
								))}
							</>
						</DSAccordion>
					))}
				</div>
			)}
			{!isData && <></>}
		</>
	);
};

export default withTranslation()(BetBuilder);
